import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { Sidebar } from 'primereact/sidebar'
import { useTranslation } from 'react-i18next'

// local imports
/* assets */
import calendarr from 'assets/icons/calendar-gray.svg'
import time from 'assets/icons/time.svg'
import location from 'assets/icons/location-marker-gray.svg'
import building from 'assets/icons/bina-gray.svg'
import lactation from 'assets/icons/baby-gray.svg'
import resIcon from 'assets/icons/lactation-res.svg'
import DateUtil from 'utils/DateUtil'
import {
  IFilteredLactationRoomList,
  IGetLactationRoomTimeTableList,
  ISaveLactationReservation,
} from 'types/LactationType'
import {
  ContentArea,
  Header,
  ImagePreview,
  InfoList,
  InfoListItem,
  ReservationSummary,
  SidebarActionArea,
  SidebarCancelButton,
  SidebarConfirmButton,
  SidebarContainer,
} from '../../gym/gym-list/styled'
import useSaveLactationRoomReservation from '../hooks/use-save-lactation-room-res'
import LactationService from 'service/LactationService'
import FavoriteIconButton from 'components/ui/FavoriteIconButton'
import AuthUtil from 'utils/AuthUtil'

interface ScheduleSidebarProps {
  openModal: () => void
  closeAfterSave: () => void
  visible: boolean
  setVisible: Dispatch<SetStateAction<boolean>>
  roomInfo: IFilteredLactationRoomList
  reservationInfo: IGetLactationRoomTimeTableList
}

export default function ScheduleSidebar({
  roomInfo,
  reservationInfo,
  visible,
  setVisible,
  openModal,
  closeAfterSave,
}: ScheduleSidebarProps) {
  const { t } = useTranslation()
  const date = DateUtil.dateFormat(new Date(reservationInfo.reservationEndHour))
  const reservationTime = `${reservationInfo.reservationStartHourStr} - ${reservationInfo.reservationEndHourStr}`

  // Lactation Room information desctructuring
  const {
    id,
    isFavorite,
    floor: { blockName, cityName, countryName, districtName, locationName, name: floorName },
  } = roomInfo || {}
  const salonName = roomInfo.name
  const salonLocationName = `${locationName}, ${districtName}, ${cityName}, ${countryName}`
  const employee = AuthUtil.getEmployeeSummary()

  /* Handle Save Lactation Room personal work reservation | Custom Hook */
  const resInfo: ISaveLactationReservation = {
    lactationRoomId: reservationInfo.lactationRoomId,
    reservationStartTime: reservationInfo.reservationStartHour,
    reservationEndTime: reservationInfo.reservationEndHour,
  }
  const { loading, saveLactationReservation } = useSaveLactationRoomReservation(resInfo, closeAfterSave)

  const closeSidebar = () => {
    setVisible(false)
    openModal()
  }

  const reservationInfoItems = useMemo(
    () => [
      {
        id: 'room-name',
        icon: lactation,
        title: t('lactation-res-modal.room-name'),
        value: salonName,
      },
      {
        id: 'location-info',
        icon: location,
        title: t('lactation-res-modal.location'),
        value: salonLocationName,
      },
      {
        id: 'block-floor-info',
        icon: building,
        title: t('lactation-res-modal.block-floor'),
        value: `${blockName} / ${floorName}`,
      },
      {
        id: 'date-info',
        icon: calendarr,
        title: t('lactation-res-modal.reservation-date'),
        value: date,
      },
      {
        id: 'time-info',
        icon: time,
        title: t('lactation-res-modal.reservation-time'),
        value: reservationTime,
      },
    ],
    [blockName, floorName, reservationTime, date, salonLocationName, salonName, t]
  )

  /* Favorite Lactation Room */
  const [favorite, setFavorite] = useState(isFavorite)
  const handleAddFavorite = () => {
    LactationService.addLactationRoomFavorite(id).then((r) => setFavorite(true))
  }
  const handleRemoveFavorite = () => {
    LactationService.removeLactationRoomFavorite(id).then((r) => setFavorite(false))
  }

  return (
    <Sidebar
      style={{ width: '500px' }}
      className="of-bordered-sidebar-box"
      visible={visible}
      position="right"
      onHide={closeSidebar}
      dismissable={false}
      closeOnEscape={false}>
      <SidebarContainer>
        <ContentArea>
          <Header className="d-flex align-items-center gap-2">
            {t('lactation-res-modal.lactation-reservation')}
            {AuthUtil.isAdmin(employee?.authList) && (
              <FavoriteIconButton onSave={handleAddFavorite} onRemove={handleRemoveFavorite} isFavorite={favorite} />
            )}
          </Header>

          <ReservationSummary>{t('lactation-res-modal.reservation-summary')}</ReservationSummary>
          <ImagePreview url={resIcon} />
          <InfoList>
            <div className="reservation-subtitle">{t('lactation-res-modal.reservation-info')}</div>
            {reservationInfoItems.map((info) => (
              <InfoListItem key={info.id}>
                <img src={info.icon} alt={info.id} />
                <span className="reservation-info-title"> {info.title} : </span>
                <span className="reservation-info-value">{info.value}</span>
              </InfoListItem>
            ))}
          </InfoList>
        </ContentArea>

        {/* Action Buttons */}
        <SidebarActionArea>
          <SidebarCancelButton
            id="backToLactationReservation"
            className="p-button-outlined"
            label={t('coiffeur-res-modal.back-to-modal')}
            onClick={closeSidebar}
          />

          <SidebarConfirmButton
            id="saveLactationReservation"
            loading={loading}
            label={t('buttons.confirm')}
            onClick={saveLactationReservation}
          />
        </SidebarActionArea>
      </SidebarContainer>
    </Sidebar>
  )
}
